/**
 * CR095 + CR096: Command Center - Unified Control Plane
 * CR118: Theme Labs moved to standalone page - redirects old deep links
 * 
 * Tabs:
 * - Deal Types: CR095 visibility controls for deal types
 * - Deal Cards: Per-type template editor
 * - Attributes: CR096 visibility controls for individual attributes
 * - Publish Gates: CR096 publish policy rules (gates + warnings)
 */

import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { 
  Search, 
  CreditCard, 
  Info, 
  RefreshCw,
  Eye,
  Clock,
  User,
  ArrowRight,
  Layers,
  Settings,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { format, formatDistanceToNow } from "date-fns";
import { AttributeVisibilityTab, PublishPolicyTab, PolicyAuditLog, DealCardsTab } from "@/components/admin/command-center";
import { RegistryTablePanel } from "@/components/admin/command-center/RegistryTablePanel";

interface VisibilityRule {
  deal_type: string;
  show_in_staging: boolean;
  show_in_admin_prod: boolean;
  show_in_public: boolean;
  updated_by: string | null;
  updated_at: string;
}

interface AuditEntry {
  id: number;
  deal_type: string;
  changed_by: string | null;
  changed_at: string;
  before_json: Record<string, unknown>;
  after_json: Record<string, unknown>;
}

interface AttributeRule {
  attribute_key: string;
  show_in_staging: boolean;
  show_in_admin_prod: boolean;
  show_in_public: boolean;
  updated_by: string | null;
  updated_at: string;
}

interface PublishRule {
  rule_id: string;
  rule_name: string;
  is_enabled: boolean;
  severity: "GATE" | "WARN";
  applies_to: "ALL" | "DEAL_TYPES";
  deal_types: string[] | null;
  rule_type: string;
  params_json: Record<string, unknown>;
  updated_by: string | null;
  updated_at: string;
}

interface PolicyAuditEntry {
  id: number;
  policy_scope: string;
  policy_key: string;
  changed_by: string | null;
  changed_at: string;
  before_json: Record<string, unknown>;
  after_json: Record<string, unknown>;
}

export default function CommandCenter() {
  const location = useLocation();
  const navigate = useNavigate();

  // CR118: Redirect old theme-labs deep link to standalone page
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("tab") === "theme-labs") {
      navigate("/admin/theme-labs", { replace: true });
    }
  }, [location.search, navigate]);

  // CR095: Deal Type Visibility state
  const [rules, setRules] = useState<VisibilityRule[]>([]);
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [updatingRows, setUpdatingRows] = useState<Set<string>>(new Set());
  const [newTypesAdded, setNewTypesAdded] = useState<string[]>([]);

  // CR096: Attribute & Publish Policy state
  const [attributeRules, setAttributeRules] = useState<AttributeRule[]>([]);
  const [publishRules, setPublishRules] = useState<PublishRule[]>([]);
  const [policyAuditLog, setPolicyAuditLog] = useState<PolicyAuditEntry[]>([]);
  const [loadingCR096, setLoadingCR096] = useState(true);

  // CR402: Registry state for Deal Types tab
  const [registry, setRegistry] = useState<import("@/providers/DealCardConfigProvider").DealTypeRegistryEntry[]>([]);
  const [loadingRegistry, setLoadingRegistry] = useState(true);

  // CR095: Fetch deal type visibility data
  const fetchDealTypeData = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("command-center-visibility", {
        method: "GET",
      });

      if (error) {
        console.error("[CommandCenter] Fetch error:", error);
        toast.error("Failed to load visibility rules");
        return;
      }

      setRules(data.rules || []);
      setAuditLog(data.audit_log || []);
      setNewTypesAdded(data.new_types_added || []);

      if (data.new_types_added?.length > 0) {
        toast.info(`${data.new_types_added.length} new deal type(s) added with safe defaults`);
      }
    } catch (err) {
      console.error("[CommandCenter] Unexpected error:", err);
      toast.error("Failed to load visibility rules");
    } finally {
      setLoading(false);
    }
  }, []);

  // CR096: Fetch attribute & publish policy data
  const fetchCR096Data = useCallback(async () => {
    setLoadingCR096(true);
    try {
      const { data, error } = await supabase.functions.invoke("command-center-attributes", {
        method: "GET",
      });

      if (error) {
        console.error("[CommandCenter] CR096 Fetch error:", error);
        toast.error("Failed to load attribute policies");
        return;
      }

      setAttributeRules(data.attribute_rules || []);
      setPublishRules(data.publish_rules || []);
      setPolicyAuditLog(data.audit_log || []);
    } catch (err) {
      console.error("[CommandCenter] CR096 Unexpected error:", err);
      toast.error("Failed to load attribute policies");
    } finally {
      setLoadingCR096(false);
    }
  }, []);

  // CR402: Fetch registry data for Deal Types tab
  const fetchRegistryData = useCallback(async () => {
    setLoadingRegistry(true);
    try {
      const { data, error } = await supabase.functions.invoke("command-center-deal-cards", {
        method: "GET",
      });

      if (error) {
        console.error("[CommandCenter] Registry fetch error:", error);
        toast.error("Failed to load registry");
        return;
      }

      setRegistry(data.registry || []);
    } catch (err) {
      console.error("[CommandCenter] Registry unexpected error:", err);
      toast.error("Failed to load registry");
    } finally {
      setLoadingRegistry(false);
    }
  }, []);

  useEffect(() => {
    fetchDealTypeData();
    fetchCR096Data();
    fetchRegistryData();
  }, [fetchDealTypeData, fetchCR096Data, fetchRegistryData]);

  // CR095: Update deal type visibility
  const updateVisibility = async (
    dealType: string,
    field: "show_in_staging" | "show_in_admin_prod" | "show_in_public",
    value: boolean
  ) => {
    // Optimistic update
    setRules((prev) =>
      prev.map((r) =>
        r.deal_type === dealType ? { ...r, [field]: value } : r
      )
    );
    setUpdatingRows((prev) => new Set([...prev, dealType]));

    try {
      const { data, error } = await supabase.functions.invoke("command-center-visibility", {
        method: "PUT",
        body: {
          deal_type: dealType,
          [field]: value,
        },
      });

      if (error) {
        console.error("[CommandCenter] Update error:", error);
        toast.error(`Failed to update ${dealType}`);
        fetchDealTypeData();
        return;
      }

      if (data.rule) {
        setRules((prev) =>
          prev.map((r) =>
            r.deal_type === dealType ? data.rule : r
          )
        );
      }

      if (data.before && data.after) {
        setAuditLog((prev) => [
          {
            id: Date.now(),
            deal_type: dealType,
            changed_by: data.after.updated_by,
            changed_at: data.after.updated_at,
            before_json: data.before,
            after_json: data.after,
          },
          ...prev.slice(0, 24),
        ]);
      }

      toast.success(`Updated ${dealType} visibility`);
    } catch (err) {
      console.error("[CommandCenter] Unexpected error:", err);
      toast.error(`Failed to update ${dealType}`);
      fetchDealTypeData();
    } finally {
      setUpdatingRows((prev) => {
        const next = new Set(prev);
        next.delete(dealType);
        return next;
      });
    }
  };

  // CR336: Only show canonical deal types, even if legacy rows somehow exist
  const CANONICAL_DEAL_TYPES = ["BONUS", "DISCOUNT", "POINTS", "IN_KIND", "UNCLASSIFIED"];
  const filteredRules = rules
    .filter((r) => CANONICAL_DEAL_TYPES.includes(r.deal_type.toUpperCase()))
    .filter((r) => r.deal_type.toLowerCase().includes(searchQuery.toLowerCase()));

  const formatAuditChange = (before: Record<string, unknown>, after: Record<string, unknown>) => {
    const changes: string[] = [];
    
    if (before.show_in_staging !== after.show_in_staging) {
      changes.push(`Staging: ${after.show_in_staging ? "ON" : "OFF"}`);
    }
    if (before.show_in_admin_prod !== after.show_in_admin_prod) {
      changes.push(`Admin: ${after.show_in_admin_prod ? "ON" : "OFF"}`);
    }
    if (before.show_in_public !== after.show_in_public) {
      changes.push(`Public: ${after.show_in_public ? "ON" : "OFF"}`);
    }
    
    return changes.join(", ") || "No changes";
  };

  // CR096: Handlers for attribute rules
  const handleAttributeUpdate = (updated: AttributeRule) => {
    setAttributeRules((prev) =>
      prev.map((r) => (r.attribute_key === updated.attribute_key ? updated : r))
    );
  };

  // CR096: Handlers for publish rules
  const handlePublishRuleUpdate = (updated: PublishRule) => {
    setPublishRules((prev) =>
      prev.map((r) => (r.rule_id === updated.rule_id ? updated : r))
    );
  };

  const handlePublishRuleAdd = (added: PublishRule) => {
    setPublishRules((prev) => [added, ...prev]);
  };

  const isLoading = loading || loadingCR096;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <CreditCard className="h-8 w-8 text-primary" />
              Deal Cards
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage deal card templates, visibility, and publish policies
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              fetchDealTypeData();
              fetchCR096Data();
              fetchRegistryData();
            }}
            disabled={isLoading || loadingRegistry}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Tabs - CR307: Deal Cards first, default tab */}
        <Tabs defaultValue="deal-cards" className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="deal-cards" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Deal Cards
            </TabsTrigger>
            <TabsTrigger value="deal-types" className="flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Deal Types
            </TabsTrigger>
            <TabsTrigger value="attributes" className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Attributes
            </TabsTrigger>
            <TabsTrigger value="publish-gates" className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4" />
              Publish Gates
            </TabsTrigger>
          </TabsList>

          {/* Tab: Deal Cards (CR098) - CR307: Now first tab and default */}
          <TabsContent value="deal-cards" className="space-y-4">
            <DealCardsTab onRefresh={() => { fetchDealTypeData(); fetchCR096Data(); }} />
          </TabsContent>

          {/* Tab: Deal Types (CR095) */}
          <TabsContent value="deal-types" className="space-y-4">
            {/* Safe Defaults Banner */}
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <strong>Safe defaults:</strong> New deal types automatically appear with{" "}
                <Badge variant="secondary" className="mx-1">Staging ON</Badge>
                <Badge variant="outline" className="mx-1">Admin OFF</Badge>
                <Badge variant="outline" className="mx-1">Public OFF</Badge>
                — review internally before enabling externally.
              </AlertDescription>
            </Alert>

            {/* New Types Added Alert */}
            {newTypesAdded.length > 0 && (
              <Alert className="border-primary/20 bg-primary/5">
                <Info className="h-4 w-4 text-primary" />
                <AlertDescription className="text-foreground">
                  New deal types discovered and added with safe defaults:{" "}
                  {newTypesAdded.map((t) => (
                    <Badge key={t} variant="secondary" className="mx-1">
                      {t}
                    </Badge>
                  ))}
                </AlertDescription>
              </Alert>
            )}

            {/* CR402: Registry Table - moved above Visibility per CR402 */}
            <RegistryTablePanel
              registry={registry}
              loading={loadingRegistry}
              onRefresh={fetchRegistryData}
            />

            {/* Visibility Rules Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Deal Type Visibility
                </CardTitle>
                <CardDescription>
                  Toggle visibility for each deal type across different surfaces.
                  Changes take effect immediately.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search deal types..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Table */}
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">Deal Type</TableHead>
                        <TableHead className="text-center">Staging</TableHead>
                        <TableHead className="text-center">Admin (Prod)</TableHead>
                        <TableHead className="text-center">Public</TableHead>
                        <TableHead className="text-right">Last Updated</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRules.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                            {searchQuery ? "No deal types match your search" : "No deal types found"}
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredRules.map((rule) => {
                          const isUpdating = updatingRows.has(rule.deal_type);
                          return (
                            <TableRow key={rule.deal_type}>
                              <TableCell className="font-medium">
                                <Badge variant="outline">{rule.deal_type}</Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <Switch
                                  checked={rule.show_in_staging}
                                  onCheckedChange={(v) =>
                                    updateVisibility(rule.deal_type, "show_in_staging", v)
                                  }
                                  disabled={isUpdating}
                                />
                              </TableCell>
                              <TableCell className="text-center">
                                <Switch
                                  checked={rule.show_in_admin_prod}
                                  onCheckedChange={(v) =>
                                    updateVisibility(rule.deal_type, "show_in_admin_prod", v)
                                  }
                                  disabled={isUpdating}
                                />
                              </TableCell>
                              <TableCell className="text-center">
                                <Switch
                                  checked={rule.show_in_public}
                                  onCheckedChange={(v) =>
                                    updateVisibility(rule.deal_type, "show_in_public", v)
                                  }
                                  disabled={isUpdating}
                                />
                              </TableCell>
                              <TableCell className="text-right text-sm text-muted-foreground">
                                {rule.updated_by && (
                                  <span className="block text-xs truncate max-w-[150px]">
                                    {rule.updated_by}
                                  </span>
                                )}
                                <span className="text-xs">
                                  {formatDistanceToNow(new Date(rule.updated_at), { addSuffix: true })}
                                </span>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Deal Type Audit Log Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Deal Type Audit Log
                </CardTitle>
                <CardDescription>
                  Recent visibility changes (last 25 entries)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-10 w-full" />
                    ))}
                  </div>
                ) : auditLog.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No audit entries yet
                  </p>
                ) : (
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-2">
                      {auditLog.map((entry) => (
                        <div
                          key={entry.id}
                          className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30"
                        >
                          <Badge variant="outline">{entry.deal_type}</Badge>
                          <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm flex-1">
                            {formatAuditChange(entry.before_json, entry.after_json)}
                          </span>
                          <div className="text-right text-xs text-muted-foreground flex-shrink-0">
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              <span className="truncate max-w-[100px]">
                                {entry.changed_by || "Unknown"}
                              </span>
                            </div>
                            <span>
                              {format(new Date(entry.changed_at), "MMM d, HH:mm")}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>

          </TabsContent>

          {/* Tab: Attributes (CR096) */}
          <TabsContent value="attributes" className="space-y-4">
            <AttributeVisibilityTab
              rules={attributeRules}
              loading={loadingCR096}
              onRefresh={fetchCR096Data}
              onUpdate={handleAttributeUpdate}
            />
            <PolicyAuditLog entries={policyAuditLog} loading={loadingCR096} />
          </TabsContent>

          {/* Tab: Publish Gates (CR096) */}
          <TabsContent value="publish-gates" className="space-y-4">
            <PublishPolicyTab
              rules={publishRules}
              loading={loadingCR096}
              onRefresh={fetchCR096Data}
              onUpdate={handlePublishRuleUpdate}
              onAdd={handlePublishRuleAdd}
            />
            <PolicyAuditLog entries={policyAuditLog} loading={loadingCR096} />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
